import { ChangeEvent, useCallback, useMemo } from "react";
import { useSearchParams } from "react-router-dom";

import { ROWS_PER_PAGE } from "@covertree/consts";
import { SortOrder } from "@covertree/types";

export const useTableViewPagination = (
  total = 0,
  rowsPerPage = ROWS_PER_PAGE,
  initialPagination: Record<"from" | "filter" | "order" | "sortBy", string>
) => {
  const [searchParams, setSearchParams] = useSearchParams();

  const fromParam = searchParams.get("from");
  const sortByParam = searchParams.get("sortBy");
  const orderParam = searchParams.get("order");
  const filterParam = searchParams.get("filter");

  const from = useMemo(() => {
    return +(fromParam ?? 0);
  }, [fromParam]);

  const pagesCount = useMemo(() => {
    return Math.ceil(total / rowsPerPage);
  }, [total, rowsPerPage]);

  const currentPage = useMemo(() => {
    return Math.ceil((from - 1) / rowsPerPage) + 1;
  }, [from, rowsPerPage]);

  const sortBy = useMemo(
    () => sortByParam ?? initialPagination.sortBy,
    [initialPagination.sortBy, sortByParam]
  );

  const order = useMemo<SortOrder>(
    () => (orderParam ?? initialPagination.order) as SortOrder,
    [initialPagination.order, orderParam]
  );

  const filter = useMemo(
    () => filterParam ?? initialPagination.filter,
    [initialPagination.filter, filterParam]
  );

  const onChangePage = useCallback(
    (e: ChangeEvent<unknown>, pageNumber: number) => {
      searchParams.set("from", ((pageNumber - 1) * rowsPerPage).toString());
      setSearchParams(searchParams);
    },
    [searchParams, setSearchParams, rowsPerPage]
  );

  const onChangeFilter = useCallback(
    (nextFilter: string) => {
      searchParams.set("filter", nextFilter);
      searchParams.set("from", initialPagination.from);
      setSearchParams(searchParams);
    },
    [initialPagination.from, searchParams, setSearchParams]
  );

  const onChangeSort = useCallback(
    (nextSortBy: string) => {
      searchParams.set("sortBy", nextSortBy);
      searchParams.set(
        "order",
        order === SortOrder.Ascending
          ? SortOrder.Descending
          : SortOrder.Ascending
      );
      setSearchParams(searchParams);
    },
    [order, searchParams, setSearchParams]
  );

  return {
    from,
    order,
    sortBy,
    filter,
    pagesCount,
    currentPage,
    onChangePage,
    onChangeFilter,
    onChangeSort,
  };
};
